import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Hammer,
  Zap,
  Paintbrush,
  Trees,
  Laptop2,
  Truck,
  Wrench,
  ClipboardList,
  Upload,
  FileText,
  Type,
  CheckCircle2,
  Coins,
  ShieldCheck,
  Search,
} from "lucide-react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api from "@/lib/api";
import RegionPicker from "@/components/RegionPicker";

const TYPES = [
  { id: "Renovatie & Bouw", icon: Hammer },
  { id: "Elektriciteit & Sanitair", icon: Zap },
  { id: "Schilderwerk & Afwerking", icon: Paintbrush },
  { id: "Tuinaanleg & Buitenwerken", icon: Trees },
  { id: "IT & Technologie", icon: Laptop2 },
  { id: "Verhuizing & Transport", icon: Truck },
  { id: "Onderhoud & Herstelling", icon: Wrench },
  { id: "Andere", icon: ClipboardList },
];

async function fileToB64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const res = reader.result;
      const idx = String(res).indexOf(",");
      resolve(idx >= 0 ? String(res).slice(idx + 1) : String(res));
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Credits() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [balance, setBalance] = useState(null);
  const [step, setStep] = useState(0); // 0 = check, 1 = fill, 2 = submitting
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [phone, setPhone] = useState("");
  const [type, setType] = useState("");
  const [regio, setRegio] = useState("");
  const [mode, setMode] = useState("file");
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const checkBalance = async (target) => {
    const t = (target || email).trim();
    if (!t) return;
    try {
      const { data } = await api.get(`/credits?email=${encodeURIComponent(t)}`);
      setBalance(data);
      if (data.credits_remaining > 0) setStep(1);
      else setStep(0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kon credits niet ophalen");
    }
  };

  useEffect(() => {
    if (params.get("email")) checkBalance(params.get("email"));
    // eslint-disable-next-line
  }, []);

  const canSubmit = useMemo(() => {
    if (!first || !last || !type || !regio) return false;
    if (mode === "file") return !!file;
    return text.trim().length >= 30;
  }, [first, last, type, regio, mode, file, text]);

  const redeem = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        email,
        offerte_type: type,
        regio,
        first_name: first,
        last_name: last,
        phone: phone || undefined,
      };
      if (mode === "file") {
        payload.upload_filename = file.name;
        payload.upload_mime = file.type;
        payload.upload_b64 = await fileToB64(file);
      } else {
        payload.text_content = text;
      }
      const { data } = await api.post("/credits/redeem", payload);
      toast.success("Credit gebruikt! Je rapport wordt nu gegenereerd.");
      navigate(`/rapport/${data.order_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Inwisselen mislukt");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <section className="oc-section">
        <div className="oc-container max-w-3xl">
          <div>
            <span className="inline-flex items-center gap-2 text-[13px] uppercase tracking-[0.1em] font-semibold text-oc-secondary">
              <Coins className="w-4 h-4" /> Credits inwisselen
            </span>
            <h1 className="font-display font-bold text-[40px] sm:text-[48px] leading-tight tracking-tight text-oc-primary mt-3">
              Gebruik je 3-pack credits
            </h1>
            <p className="mt-4 text-oc-textSecondary text-[17px] leading-relaxed">
              Je hebt een 3-pack aangekocht? Hieronder kan je je credits gebruiken om een nieuwe offerte gratis te laten analyseren.
            </p>
          </div>

          <div className="mt-10 bg-white border border-oc-border rounded-oc p-7 space-y-4">
            <Label>Je e-mailadres</Label>
            <div className="flex gap-2">
              <Input
                data-testid="credits-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="je@email.be"
                className="h-12 rounded-oc-btn"
              />
              <Button
                onClick={() => checkBalance()}
                data-testid="credits-check"
                className="bg-oc-primary hover:bg-oc-secondary text-white rounded-oc-btn h-12 px-6 font-semibold"
              >
                <Search className="w-4 h-4 mr-2" /> Controleer
              </Button>
            </div>
            {balance && (
              <p className="text-[14px] text-oc-textPrimary">
                Saldo: <strong className="font-mono text-oc-primary">{balance.credits_remaining}</strong> credit(s){" "}
                {balance.expires_at && (
                  <span className="text-oc-textSecondary">
                    · geldig tot {new Date(balance.expires_at).toLocaleDateString("nl-BE")}
                  </span>
                )}
              </p>
            )}
          </div>

          {step === 1 && balance?.credits_remaining > 0 && (
            <div className="mt-6 bg-white border border-oc-border rounded-oc p-7 space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Voornaam</Label>
                  <Input data-testid="credits-first" value={first} onChange={(e) => setFirst(e.target.value)} className="h-12 rounded-oc-btn" />
                </div>
                <div className="space-y-2">
                  <Label>Familienaam</Label>
                  <Input data-testid="credits-last" value={last} onChange={(e) => setLast(e.target.value)} className="h-12 rounded-oc-btn" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Telefoonnummer (optioneel)</Label>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} className="h-12 rounded-oc-btn" />
              </div>

              <div className="space-y-2">
                <Label>Type offerte</Label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {TYPES.map((t) => {
                    const Icon = t.icon;
                    const active = type === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => setType(t.id)}
                        className={`p-3 rounded-oc-btn border text-left transition-colors ${
                          active ? "border-oc-primary bg-oc-primary text-white" : "border-oc-border bg-white text-oc-primary hover:bg-oc-subtle"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <p className="text-[12px] font-semibold mt-1 leading-tight">{t.id}</p>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Regio</Label>
                <RegionPicker value={regio} onChange={setRegio} testIdPrefix="credits-region" />
              </div>

              <div className="flex bg-oc-subtle p-1 rounded-oc-btn w-fit">
                <button onClick={() => setMode("file")} className={`px-4 py-1.5 text-[13px] font-semibold rounded-md flex items-center gap-2 ${mode === "file" ? "bg-oc-primary text-white" : "text-oc-textSecondary"}`}>
                  <Upload className="w-3.5 h-3.5" /> Bestand
                </button>
                <button onClick={() => setMode("text")} className={`px-4 py-1.5 text-[13px] font-semibold rounded-md flex items-center gap-2 ${mode === "text" ? "bg-oc-primary text-white" : "text-oc-textSecondary"}`}>
                  <Type className="w-3.5 h-3.5" /> Tekst
                </button>
              </div>

              {mode === "file" ? (
                <label htmlFor="credit-file" className="block bg-white border-2 border-dashed border-oc-border rounded-oc p-8 text-center cursor-pointer hover:border-oc-secondary">
                  {file ? (
                    <div className="flex items-center justify-center gap-3 text-oc-primary">
                      <FileText className="w-5 h-5" />
                      <span>{file.name}</span>
                      <CheckCircle2 className="w-4 h-4 text-oc-fair" />
                    </div>
                  ) : (
                    <>
                      <Upload className="w-6 h-6 text-oc-primary mx-auto" />
                      <p className="mt-3 font-semibold text-oc-primary">PDF, JPG of PNG (max 20 MB)</p>
                    </>
                  )}
                  <input
                    id="credit-file"
                    type="file"
                    accept=".pdf,image/jpeg,image/png,image/heic,image/heif"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                </label>
              ) : (
                <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Plak hier de offerte tekst..." className="min-h-[200px] rounded-oc-btn" />
              )}

              <Button data-testid="credits-redeem" disabled={!canSubmit || submitting} onClick={redeem} className="w-full h-13 bg-oc-primary hover:bg-oc-secondary text-white rounded-oc-btn font-semibold">
                {submitting ? "Bezig..." : "Gebruik 1 credit & analyseer offerte"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {balance && balance.credits_remaining === 0 && (
            <div className="mt-6 bg-oc-subtle border border-oc-border rounded-oc p-7 flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-oc-secondary mt-1" />
              <div>
                <p className="font-display font-semibold text-oc-primary">Geen actieve credits</p>
                <p className="text-oc-textSecondary text-[14px] mt-1">
                  Geen 3-pack op dit e-mailadres. Wil je er één aanschaffen?
                </p>
                <Link to="/analyse" className="inline-block mt-3">
                  <Button variant="outline" className="rounded-oc-btn border-oc-border text-oc-primary hover:bg-white">
                    <ArrowLeft className="w-4 h-4 mr-2" /> Naar de prijzen
                  </Button>
                </Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
